import { type FC } from "react";
import { Plus, X } from "lucide-react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { YearInput } from "@/shared/components/ui/YearInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import type { FamilyTriagePersonRow, PersonFlowStatus } from "@/shared/types/probateTypes";

import { NOT_SURE_CAPTION } from "./nameUtils";

type RowVariant = "full" | "parent";

interface IssueRowProps {
  row: FamilyTriagePersonRow;
  onChange: (updated: FamilyTriagePersonRow) => void;
  onRemove: () => void;
  depth?: number;
  variant?: RowVariant;
  showMinor?: boolean;
  submitted?: boolean;
}

function createEmptyRow(): FamilyTriagePersonRow {
  return {
    id: crypto.randomUUID(),
    name: "",
    isMinor: "",
    status: "",
    deathYear: "",
    hasChildren: "",
    children: [],
  };
}

export const IssueRow: FC<IssueRowProps> = ({
  row,
  onChange,
  onRemove,
  depth = 0,
  variant = "full",
  showMinor = true,
  submitted = false,
}) => {
  const isParent = variant === "parent";
  const showMinorField = showMinor && !isParent;
  const showPassedAwayBlock = row.status === "passed_away" && !isParent;

  const nameError = submitted && !row.name.trim() ? "Name is required" : undefined;
  const statusError = submitted && !row.status ? "Status is required" : undefined;

  const updateField = <K extends keyof FamilyTriagePersonRow>(
    field: K,
    value: FamilyTriagePersonRow[K],
  ) => {
    onChange({ ...row, [field]: value });
  };

  const handleStatusChange = (value: string) => {
    const status = value as PersonFlowStatus;
    if (status === "passed_away") {
      updateField("status", status);
    } else {
      onChange({
        ...row,
        status,
        deathYear: "",
        hasChildren: "",
        children: [],
      });
    }
  };

  const handleChildChange = (index: number, updated: FamilyTriagePersonRow) => {
    const next = [...row.children];
    next[index] = updated;
    updateField("children", next);
  };

  const handleChildRemove = (index: number) => {
    const next = row.children.filter((_, i) => i !== index);
    onChange({ ...row, children: next, hasChildren: next.length === 0 ? "" : row.hasChildren });
  };

  const handleAddChild = () => {
    updateField("children", [...row.children, createEmptyRow()]);
  };

  return (
    <div className={depth > 0 ? "ml-6 border-l border-primary pl-4" : ""}>
      <div className="flex items-start gap-3 rounded-lg border border-primary bg-white p-4">
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Full legal name</Label>
            <Input
              placeholder="First and last name"
              value={row.name}
              onChange={(e) => updateField("name", e.target.value)}
              className={nameError ? "border-destructive" : ""}
            />
            <FieldError message={nameError} />
            {row.sourceTag && (
              <p className="text-xs text-secondary">{row.sourceTag}</p>
            )}
          </div>

          <div className={showMinorField ? "grid grid-cols-2 gap-3" : ""}>
            {showMinorField && (
              <div className="flex flex-col gap-1.5">
                <Label>Under 18?</Label>
                <Select
                  value={row.isMinor}
                  onValueChange={(v) => updateField("isMinor", v as "yes" | "no")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <Select value={row.status} onValueChange={handleStatusChange}>
                <SelectTrigger className={statusError ? "border-destructive" : ""}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="living">Living</SelectItem>
                  <SelectItem value="passed_away">Passed away</SelectItem>
                  <SelectItem value="not_sure">Not sure</SelectItem>
                </SelectContent>
              </Select>
              <FieldError message={statusError} />
            </div>
          </div>

          {showPassedAwayBlock && (
            <p className="text-xs text-secondary">
              The court asks for a few extra details, and you'll be guided through each one.
            </p>
          )}

          {row.status === "not_sure" && (
            <p className="text-xs text-secondary">{NOT_SURE_CAPTION}</p>
          )}
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="mt-1 shrink-0 rounded-md p-1 text-tertiary hover:bg-subtle hover:text-primary"
          aria-label="Remove"
        >
          <X className="size-4" />
        </button>
      </div>

      {showPassedAwayBlock && (
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-primary bg-subtle/50 p-4">
          <div className="flex flex-col gap-1.5">
            <Label>What year did they pass?</Label>
            <YearInput
              value={row.deathYear}
              onChange={(v) => updateField("deathYear", v)}
            />
            <p className="text-xs text-secondary">
              A best guess is fine, and it can be left blank.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Did they have any children?</Label>
            <p className="text-xs text-secondary">
              If they did, their children take that place on the court's list.
            </p>
            <Select
              value={row.hasChildren}
              onValueChange={(v) => {
                const val = v as "yes" | "no" | "not_sure";
                if (val === "yes" && row.children.length === 0) {
                  onChange({ ...row, hasChildren: val, children: [createEmptyRow()] });
                } else if (val !== "yes") {
                  onChange({ ...row, hasChildren: val, children: [] });
                } else {
                  updateField("hasChildren", val);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="not_sure">Not sure</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {row.hasChildren === "yes" && (
            <div className="flex flex-col gap-3">
              {row.children.map((child, i) => (
                <IssueRow
                  key={child.id}
                  row={child}
                  onChange={(updated) => handleChildChange(i, updated)}
                  onRemove={() => handleChildRemove(i)}
                  depth={depth + 1}
                  submitted={submitted}
                />
              ))}
              <button
                type="button"
                onClick={handleAddChild}
                className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
              >
                <Plus className="size-3.5" />
                Add another child
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { createEmptyRow };
export type { IssueRowProps };
